/**
 * Broad to SKAG Extractor — секция Event Cost Guard (ТЗ BTSE §2).
 *
 * Чистые правила экрана: наследование default, автоподстановка (результат
 * считает бэк, здесь только применение), групповые действия, фильтр
 * таблицы стран. Отсутствие страны в `overrides` = «наследует default»,
 * `null` = явное «No ECG» — различаются везде.
 */

import type { BtseEcgConfig, BtseEcgOption } from "@/lib/api-hooks";

export const GTG_TIP = "Event Cost Guard and Go-to-Goal can't be applied to the same keyword";
export const ECG_IN_APPLY_TIP = "Event Cost Guard is set per country below";
export const ROW_UNAVAILABLE =
  "Rule unavailable — keywords in this country will start without ECG";
export const CARD_UNAVAILABLE = "Rule unavailable — keyword will start without ECG";

export function emptyEcg(): BtseEcgConfig {
  return { enabled: false, default_rule_id: null, overrides: {} };
}

/** Автоподстановка ставится только странам, которые сейчас наследуют
 *  default: ручное переопределение и явное No ECG не трогаются. */
export function applyAutofill(
  overrides: Record<string, number | null>,
  autofill: Record<string, number>,
): Record<string, number | null> {
  const next = { ...overrides };
  for (const [cc, id] of Object.entries(autofill)) {
    if (!(cc in next)) next[cc] = id;
  }
  return next;
}

/** Эффективный ECG страны: своё переопределение или default. */
export function effectiveEcg(cfg: BtseEcgConfig, code: string): number | null {
  return code in cfg.overrides ? cfg.overrides[code] : cfg.default_rule_id;
}

export function assign(
  overrides: Record<string, number | null>,
  codes: string[],
  id: number | null,
): Record<string, number | null> {
  const next = { ...overrides };
  for (const c of codes) next[c] = id;
  return next;
}

export function resetToDefault(
  overrides: Record<string, number | null>,
  codes: string[],
): Record<string, number | null> {
  const next = { ...overrides };
  for (const c of codes) delete next[c];
  return next;
}

export type CountryRow = { code: string; name: string };

export function visibleCountries(
  countries: CountryRow[],
  search: string,
  overriddenOnly: boolean,
  overrides: Record<string, number | null>,
): CountryRow[] {
  const q = search.trim().toLowerCase();
  return countries.filter(
    (c) =>
      (!q || c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)) &&
      (!overriddenOnly || c.code in overrides),
  );
}

export type EcgLabel = { text: string; unavailable: boolean };

/** Подпись значения в триггере: имя, «No ECG», зачёркнутое недоступное. */
export function ecgLabel(id: number | null, options: BtseEcgOption[]): EcgLabel {
  if (id == null) return { text: "No ECG", unavailable: false };
  const o = options.find((x) => x.id === id);
  if (!o) return { text: "Deleted rule", unavailable: true };
  return { text: o.name, unavailable: o.status !== "active" };
}

/** id, сохранённые в конфиге (нужны бэку, чтобы вернуть удалённые). */
export function referencedIds(cfg: BtseEcgConfig): number[] {
  const out = new Set<number>();
  if (cfg.default_rule_id != null) out.add(cfg.default_rule_id);
  for (const v of Object.values(cfg.overrides)) if (v != null) out.add(v);
  return [...out];
}

/** Флаг из стрелки ISO-кода (региональные индикаторы). */
export function flagOf(code: string): string {
  return String.fromCodePoint(...[...code.toUpperCase()].map((ch) => 127397 + ch.charCodeAt(0)));
}

type CircleLike = { actions?: Array<{ object?: string | null } | null> | null } | null | undefined;
export type RuleLike = {
  preset?: string | null;
  circle_1_config?: CircleLike;
  circle_2_config?: CircleLike;
};

const GOAL_OBJECTS = new Set(["Go to: Goal 1", "Go to: Goal 2", "Go to: Goal 3"]);

/** Класс правила для взаимоисключения (зеркало `incompatible.rule_class`). */
export function ruleClass(r: RuleLike): "ecg" | "goal" | null {
  if (r.preset === "event_cost_guard") return "ecg";
  for (const c of [r.circle_1_config, r.circle_2_config]) {
    for (const a of c?.actions ?? []) if (a?.object && GOAL_OBJECTS.has(a.object)) return "goal";
  }
  return null;
}

/** Почему тумблер ECG заблокирован выбранными «Apply rules after
 *  creation» (null — не заблокирован). */
export function ecgToggleLock(selected: RuleLike[]): string | null {
  const classes = selected.map(ruleClass);
  if (classes.includes("goal")) return GTG_TIP;
  if (classes.includes("ecg")) return ECG_IN_APPLY_TIP;
  return null;
}

/** Почему правило нельзя выбрать в «Apply rules after creation» при
 *  включённом ECG (null — можно). */
export function applyAfterLock(rule: RuleLike, ecgOn: boolean): string | null {
  if (!ecgOn) return null;
  const c = ruleClass(rule);
  if (c === "goal") return GTG_TIP;
  if (c === "ecg") return ECG_IN_APPLY_TIP;
  return null;
}

/** События экрана, после которых применяется автоподстановка (ТЗ §2.2):
 *  только переход тумблера off→on и смена приложения при включённом
 *  тумблере. Открытие сохранённого пресета — никогда. */
export type AutofillEvent = "open" | "toggle" | "app_change";
export function armsAutofill(ev: AutofillEvent, enabledBefore: boolean, enabledAfter: boolean): boolean {
  if (ev === "toggle") return !enabledBefore && enabledAfter;
  if (ev === "app_change") return enabledAfter;
  return false;
}
